import { ChangeEvent, useCallback, useMemo } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';

import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import SearchableFilter from 'components/SearchableFilter';
import PaginationFooter from 'components/PaginationFooter';
import * as ManagementLayout from 'components/ManagementLayout';
import FleetAdminAction from 'components/FleetAdminAction';

import { useCurrentUserContext } from 'utils/User';
import useFleetAdminPermissions from 'utils/User/useFleetAdminPermissions';

import { SearchProvider } from 'contexts/SearchContext';

import EmptyResults from 'components/EmptyResults';
import Search from '@decisiv/iconix/lib/components/Search';
import useToggle from 'hooks/useToggle';
import { NoWrapHeaderTable, Separator } from '../components/styles';
import { useManagedPlatformAccountsQuery } from './hooks/useManagedPlatformAccountsQuery';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from '../Teams/hooks/useTeamsFilters';

import useAccountsFilters from './hooks/useAccountsFilters';
import { generateColumns } from './columns';
import { ClearAllButton } from '../styles';
import useTableUrlState from '../hooks/useTableUrlState';
import CreateAccountModal from './CreateAccountModal';
import BulkAddAssetButton from './BulkAddAssetButton';

export type ManagedFleet = ExtractFromArray<
  ManagedPlatformAccountsQuery['managedPlatformAccounts']['collection']
>;

const Fleets = () => {
  const [createAccountModalOpen, toggleCreateAccountModal] = useToggle();
  const fleetAdminPermissions = useFleetAdminPermissions();
  const createAccountCapability =
    fleetAdminPermissions.anySegment('canCreateAccount');

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleChangeTeamsFilter,
    handleChangeAccountsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const {
    page,
    setPage,
    apiSort,
    defaultSort,
    onSort,
    liveSearch,
    setLiveSearch,
    debouncedSearch,
  } = useTableUrlState<ManagedPlatformAccountsSortColumnEnum>(
    'management-accounts',
  );

  const { oemSegments } = useCurrentUserContext();
  const amountOfOemSegmentsManaged = oemSegments?.length ?? 0;

  const handleOnTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setLiveSearch(e.target.value);
    },
    [setLiveSearch],
  );

  const { data, loading } = useManagedPlatformAccountsQuery({
    variables: {
      sort: apiSort,
      page,
      ids: pageParams.accountIds,
      sentinelTeamsIds: pageParams.teamIds,
      oemSegmentsIds: pageParams.oemSegmentIds,
      text: debouncedSearch.length > 2 ? debouncedSearch : undefined,
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    oemSegmentIds: pageParams.oemSegmentIds,
    onSubmit: handleChangeTeamsFilter,
  });

  const accountsFilters = useAccountsFilters({
    value: pageParams.accountIds,
    oemSegmentsIds: pageParams.oemSegmentIds,
    sentinelTeamsIds: pageParams.teamIds,
    onSubmit: handleChangeAccountsFilter,
  });

  const columns = useMemo(
    () => generateColumns({ onSort, amountOfOemSegmentsManaged }),
    [onSort, amountOfOemSegmentsManaged],
  );

  const tableData = data?.managedPlatformAccounts.collection ?? [];
  const pagination = data?.managedPlatformAccounts.pagination;

  return (
    <>
      <ManagementLayout.TabBody.Container>
        <ManagementLayout.TabBody.Header.Container>
          <ManagementLayout.TabBody.Header.Left>
            <FleetAdminAction capability={createAccountCapability}>
              {({ disabled }) => (
                <Button
                  text={t`Create Account`}
                  icon={Plus}
                  size="small"
                  disabled={disabled}
                  onClick={toggleCreateAccountModal}
                />
              )}
            </FleetAdminAction>
            <Separator />
            <BulkAddAssetButton />
          </ManagementLayout.TabBody.Header.Left>
          <ManagementLayout.TabBody.Header.Right>
            <P size="small" marginRight={1}>
              <Trans>Search by Account Name, Account ID, and City:</Trans>
            </P>
            <SearchInput
              value={liveSearch}
              onChange={handleOnTextChange}
              hideLabel
              aria-label={t`Search by Account Name, Account ID, and City`}
              name="searchByFleet"
              size="small"
            />
          </ManagementLayout.TabBody.Header.Right>
        </ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Content.Container>
          <ManagementLayout.TabBody.Content.Filters>
            {amountOfOemSegmentsManaged > 1 && (
              <SearchableFilter
                popoverLabel={t`OEM Segments`}
                placement="bottom-start"
                {...oemSegmentsFilterProps}
              />
            )}
            <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
            <SearchableFilter popoverLabel={t`Accounts`} {...accountsFilters} />
            {isFiltersDirty && (
              <ClearAllButton
                variant="ghost"
                onClick={handleClearFilters}
                text={t`Clear All`}
                size="small"
              />
            )}
          </ManagementLayout.TabBody.Content.Filters>
          {loading && (
            <Loading
              flex={1}
              justifyContent="center"
              size="large"
              title={t`Loading...`}
            />
          )}
          {!loading && tableData.length === 0 && (
            <EmptyResults
              title={t`No Accounts Found`}
              subtitle={t`No Managed Accounts Found`}
              icon={Search}
            />
          )}
          {!loading && tableData.length > 0 && (
            <SearchProvider searchString={debouncedSearch}>
              <NoWrapHeaderTable
                height="100%"
                scroll={['x', 'y']}
                data={tableData}
                columns={columns}
                onSort={onSort}
                defaultSort={defaultSort}
                footer={() => (
                  <PaginationFooter
                    // @ts-expect-error - JS file
                    title={t`Accounts`}
                    setCurrentPage={setPage}
                    totalOnPage={tableData.length}
                    page={pagination?.page}
                    perPage={pagination?.perPage}
                    totalEntries={pagination?.totalEntries}
                    totalPages={pagination?.totalPages}
                  />
                )}
              />
            </SearchProvider>
          )}
        </ManagementLayout.TabBody.Content.Container>
      </ManagementLayout.TabBody.Container>
      {createAccountModalOpen && (
        <CreateAccountModal onClose={toggleCreateAccountModal} />
      )}
    </>
  );
};

export default Fleets;
